import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import {
  marcarLeidaEnLista,
  eliminarDeLista,
  formatearFechaRelativa,
} from "../../utils/notifications.util";
import { listenForegroundMessages } from "../../firebase";

const API = process.env.REACT_APP_API_URL;

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const cargarContador = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(res.data.count);
    } catch {
      // silencioso: no bloquear la UI por esto
    }
  }, [token]);

  const cargarLista = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.data);
    } catch {
      // silencioso
    }
  }, [token]);

  useEffect(() => {
    cargarContador();
  }, [cargarContador]);

  useEffect(() => {
    listenForegroundMessages(() => {
      cargarContador();
      cargarLista();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) cargarLista();
  };

  const handleClickItem = async (item) => {
    setItems((prev) => marcarLeidaEnLista(prev, item.id));
    if (!item.read) setUnreadCount((c) => Math.max(c - 1, 0));
    setOpen(false);
    navigate(item.ruta);
    try {
      await axios.patch(
        `${API}/notifications/${item.id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      // silencioso
    }
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    setItems((prev) => eliminarDeLista(prev, item.id));
    if (!item.read) setUnreadCount((c) => Math.max(c - 1, 0));
    try {
      await axios.delete(`${API}/notifications/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // silencioso
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[1rem] px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-96 overflow-y-auto">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-sm font-semibold text-gray-800">Notificaciones</p>
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">
              No tenés notificaciones
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                onClick={() => handleClickItem(item)}
                className={`flex items-start justify-between gap-2 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-blue-50 ${
                  item.read ? "" : "bg-blue-50/40"
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {item.titulo}
                  </p>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {item.mensaje}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {formatearFechaRelativa(item.createdAt)}
                  </p>
                </div>
                <button
                  onClick={(e) => handleDelete(e, item)}
                  className="shrink-0 p-1 text-gray-400 hover:text-red-500"
                  aria-label="Eliminar"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
